import { ArrowLeft, Clock, Droplet, Gauge, Pencil, Plus, Trash2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { LocacoesStatCard } from "./LocacoesStatCard";
import {
  COMBUSTIVEL_LABELS,
  MANUTENCAO_STATUS_LABELS,
  MAQUINARIO_STATUS_LABELS,
  type AbastecimentoDto,
  type LocacaoDto,
  type ManutencaoDto,
  type MaquinarioDto,
} from "@/services/locacoes";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import {
  filterAbastecimentosMaquina,
  filterManutencoesAbertasMaquina,
  getUltimaLocacaoEncerrada,
  locacoesPanelClass,
  locacoesSectionTitleClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";
import { formatDate, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";

interface Props {
  maquina: MaquinarioDto;
  locacoes: LocacaoDto[];
  abastecimentos: AbastecimentoDto[];
  manutencoes: ManutencaoDto[];
  onVoltar: () => void;
  onNovoAbastecimento: () => void;
  onRegistrarManutencao: () => void;
  onEditAbastecimento?: (item: AbastecimentoDto) => void;
  onDeleteAbastecimento?: (item: AbastecimentoDto) => void;
}

export function GaragemMaquinaDetail({
  maquina,
  locacoes,
  abastecimentos,
  manutencoes,
  onVoltar,
  onNovoAbastecimento,
  onRegistrarManutencao,
  onEditAbastecimento,
  onDeleteAbastecimento,
}: Props) {
  const theme = MAQUINARIO_STATUS_THEME[maquina.status];
  const ultimaLoc = getUltimaLocacaoEncerrada(maquina.id, locacoes);
  const historicoAbast = filterAbastecimentosMaquina(abastecimentos, maquina.id);
  const manutAbertas = filterManutencoesAbertasMaquina(maquina.id, manutencoes);
  const litros = historicoAbast.reduce((s, a) => s + a.litros, 0);
  const valor = historicoAbast.reduce((s, a) => s + a.valorTotal, 0);

  return (
    <div className={locacoesTabContentClass}>
      <div className={cn(locacoesPanelClass, "border-l-4 border-l-sky-400")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onVoltar}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="-ml-2"
            >
              Voltar à garagem
            </Button>
            <h2 className="text-lg font-bold text-slate-800">{maquina.nome}</h2>
            <p className="text-sm text-slate-500">
              {[maquina.tipo, maquina.codigoInterno, maquina.placa].filter(Boolean).join(" · ") || "—"}
            </p>
            <span
              className={cn(
                "mt-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                theme.badge,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", theme.dot)} />
              {MAQUINARIO_STATUS_LABELS[maquina.status]}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" leftIcon={<Droplet className="h-4 w-4" />} onClick={onNovoAbastecimento}>
              Novo abastecimento
            </Button>
            <Button leftIcon={<Wrench className="h-4 w-4" />} onClick={onRegistrarManutencao}>
              Registrar manutenção
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard
          title="Horímetro atual"
          value={`${formatNumber(maquina.horimetroAtual, 1)} h`}
          icon={<Gauge className="h-5 w-5" />}
          accentClass="border-l-4 border-l-blue-400"
        />
        <LocacoesStatCard
          title="Última locação"
          value={ultimaLoc ? formatDate(ultimaLoc.dataFimReal ?? ultimaLoc.dataInicio) : "—"}
          icon={<Clock className="h-5 w-5" />}
          accentClass="border-l-4 border-l-purple-400"
        />
        <LocacoesStatCard
          title="Manutenções abertas"
          value={manutAbertas.length}
          icon={<Wrench className="h-5 w-5" />}
          accentClass="border-l-4 border-l-amber-400"
        />
        <LocacoesStatCard
          title="Abastecimentos"
          value={historicoAbast.length}
          subtitle={`${formatNumber(litros, 0)} L · R$ ${formatNumber(valor, 2)}`}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
      </div>

      {manutAbertas.length > 0 && (
        <section className="flex flex-col gap-4">
          <div className={locacoesSectionTitleClass}>
            <Wrench className="h-4 w-4 text-amber-600" />
            <h3>Manutenções em aberto</h3>
          </div>
          <div className={locacoesTableWrapClass}>
            <table className="min-w-full text-sm">
              <thead className={locacoesTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Descrição</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Abertura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {manutAbertas.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-3 capitalize text-slate-700">{m.tipo.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-slate-600">{m.descricao}</td>
                    <td className="px-4 py-3">{MANUTENCAO_STATUS_LABELS[m.status]}</td>
                    <td className="px-4 py-3">{formatDate(m.dataAbertura)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className={locacoesSectionTitleClass}>
            <Droplet className="h-4 w-4 text-sky-600" />
            <h3>Histórico de abastecimentos</h3>
          </div>
          <Button size="sm" variant="secondary" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={onNovoAbastecimento}>
            Registrar abastecimento
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <LocacoesStatCard
            title="Registros"
            value={historicoAbast.length}
            icon={<Droplet className="h-5 w-5" />}
            accentClass="border-l-4 border-l-sky-400"
          />
          <LocacoesStatCard
            title="Litros"
            value={`${formatNumber(litros, 0)} L`}
            icon={<Droplet className="h-5 w-5" />}
            accentClass="border-l-4 border-l-orange-400"
          />
          <LocacoesStatCard
            title="Valor total"
            value={`R$ ${formatNumber(valor, 2)}`}
            icon={<Droplet className="h-5 w-5" />}
            accentClass="border-l-4 border-l-emerald-400"
          />
        </div>

        <div className={locacoesTableWrapClass}>
          <table className="min-w-full text-sm">
            <thead className={locacoesTableHeadClass}>
              <tr>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Litros</th>
                <th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3">Combustível</th>
                <th className="px-4 py-3">Horímetro</th>
                <th className="px-4 py-3">L/h</th>
                <th className="px-4 py-3">Responsável</th>
                {(onEditAbastecimento || onDeleteAbastecimento) && (
                  <th className="px-4 py-3 text-right">Ações</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {historicoAbast.length === 0 ? (
                <tr>
                  <td
                    colSpan={onEditAbastecimento || onDeleteAbastecimento ? 8 : 7}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    Nenhum abastecimento registrado para esta máquina.
                  </td>
                </tr>
              ) : (
                historicoAbast.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">{formatDate(a.data)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatNumber(a.litros, 1)} L</td>
                    <td className="px-4 py-3 tabular-nums">R$ {formatNumber(a.valorTotal, 2)}</td>
                    <td className="px-4 py-3">{COMBUSTIVEL_LABELS[a.tipoCombustivel]}</td>
                    <td className="px-4 py-3 tabular-nums">{formatNumber(a.horimetroAtual, 1)} h</td>
                    <td className="px-4 py-3 tabular-nums">
                      {a.litrosPorHora != null ? `${formatNumber(a.litrosPorHora, 1)} L/h` : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.usuarioNome}</td>
                    {(onEditAbastecimento || onDeleteAbastecimento) && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {onEditAbastecimento && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditAbastecimento(a)}
                              leftIcon={<Pencil className="h-3.5 w-3.5" />}
                            >
                              Editar
                            </Button>
                          )}
                          {onDeleteAbastecimento && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteAbastecimento(a)}
                              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                            >
                              Excluir
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
